// Web-based filesystem effect using IndexedDB for file storage

const BASE_DB_NAME = "switch-filesystem";
const DB_VERSION = 1;
const STORE_NAME = "files";

let dbPromise: Promise<IDBDatabase> | null = null;

const getDbName = (): string => {
  if (typeof window === "undefined" || !window.location?.hostname) {
    return `${BASE_DB_NAME}:default`;
  }
  const host = window.location.hostname.toLowerCase().replace(/[^a-z0-9.-]/g, "_");
  return `${BASE_DB_NAME}:${host}`;
};

const getDb = (): Promise<IDBDatabase> => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(getDbName(), DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "path" });
        }
      };
    });
  }
  return dbPromise;
};

const filesystem = {
  async writeFile({
    blob,
    path,
  }: {
    blob: Blob;
    path: string;
  }): Promise<void> {
    const db = await getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.put({ path, blob, createdAt: Date.now() });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  },

  async readFileToBlob({ path }: { path: string }): Promise<Blob | null> {
    const db = await getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(path);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.blob : null);
      };
    });
  },

  async fileExists(path: string): Promise<boolean> {
    const db = await getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(path);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        resolve(!!request.result);
      };
    });
  },

  async deleteFile(path: string): Promise<void> {
    const db = await getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(path);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  },

  async listFiles(): Promise<string[]> {
    const db = await getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAllKeys();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        resolve(request.result as string[]);
      };
    });
  },

  async getFileMetadata(
    path: string
  ): Promise<{ path: string; size: number; createdAt: number } | null> {
    const db = await getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(path);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            path: result.path,
            size: result.blob?.size || 0,
            createdAt: result.createdAt,
          });
        } else {
          resolve(null);
        }
      };
    });
  },

  async clearAll(): Promise<void> {
    const db = await getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  },
};

export default filesystem;
